import { Carrier } from 'interfaces/Carrier.interface';
import type { CarrierOrg } from 'interfaces/CarrierOrg.interface';
import Shipment from 'interfaces/PmParcel';

// Regex to detect dangerous protocols (case-insensitive)
const invalidProtocolRegex = /^([^\w]*)(javascript|data|vbscript)/im;

/**
 * Get Default Carrier (MC or OCC) from shipment data
 * Priority: shipment.parcel.carrier > shipment.org_carrier_config.carrier
 * Can return undefined if both carrier and carrierOrg.carrier are null/undefined
 */
export const getDefaultCarrier = (
  carrier: Carrier,
  carrierOrg: CarrierOrg,
): Carrier | undefined => {
  return carrier || carrierOrg?.carrier;
};

/**
 * Sanitizes URLs to prevent XSS attacks by blocking dangerous protocols
 * Checks for javascript:, data:, and vbscript: protocols (including encoded versions)
 *
 * @param url - URL to sanitize
 * @returns Sanitized URL or empty string if dangerous protocol detected
 *
 * @example
 * sanitizeUrl('https://example.com') // 'https://example.com'
 * sanitizeUrl('javascript:alert(1)') // ''
 * sanitizeUrl('%6A%61%76%61%73%63%72%69%70%74') // ''
 */
export const sanitizeUrl = (url?: string): string => {
  try {
    const trimmedURL = url?.trim();
    if (!trimmedURL) return '';

    // Decode URL
    let decodedUrl = trimmedURL;
    decodedUrl = decodeURIComponent(decodedUrl);

    // Check both original and decoded URL for dangerous protocols
    if (
      invalidProtocolRegex.test(trimmedURL) ||
      invalidProtocolRegex.test(decodedUrl)
    ) {
      return '';
    }

    return trimmedURL;
  } catch (error) {
    return '';
  }
};

/**
 * Build carrier information with OCC (Org Carrier Config) overrides
 *
 * Data sources:
 * - Logo, Name: From master carrier data (cannot be overridden)
 * - Phone, Tracking URL: From carrier data, but can be overridden by OCC
 *
 * OCC Override Logic:
 * - If merchant configured values in OCC (carrierReferenceCsPhone, carrierReferenceTrackingUrl)
 * - AND the carrier IDs match, then override the carrier's default values
 *
 * Security:
 * - All tracking URLs are sanitized to prevent XSS attacks
 * - Returns a default carrier object if carrier is null/undefined
 */
export const buildCarrierInfo = (
  carrier: Carrier | undefined,
  carrierConfig: CarrierOrg | undefined,
): Carrier | undefined => {
  // Return default carrier object if carrier is null/undefined
  if (!carrier && !carrierConfig) {
    return undefined;
  }

  // Apply OCC overrides: OCC values take priority over carrier defaults
  const phone = carrierConfig?.carrierReferenceCsPhone || carrier?.phone;
  const rawUrlTracking =
    carrierConfig?.carrierReferenceTrackingUrl ||
    carrier?.urlTracking ||
    carrier?.url;

  // Sanitize URL to prevent XSS attacks
  const urlTracking = sanitizeUrl(rawUrlTracking);

  return {
    ...carrier,
    phone,
    urlTracking,
  };
};

/**
 * Formats carrier tracking URL by replacing placeholders with shipment data
 * and sanitizes the result to prevent XSS attacks
 *
 * @param carrierUrlTracking - Template URL with placeholders
 * @param parcelId - Parcel ID to replace {{Parcel.ID}}
 * @param shipment - Shipment data for other placeholders
 * @returns Formatted and sanitized tracking URL
 */
export const formatCarrierUrlTracking = (
  carrierUrlTracking: string | undefined,
  parcelId: string,
  shipment: Shipment,
): string => {
  // Return empty string if carrierUrlTracking is null/undefined
  if (!carrierUrlTracking) {
    return '';
  }

  const {
    parcelAlias2,
    toAddress,
    parcelToPostcodeCustomer,
    customFields,
    additionalData,
  } = shipment;

  const toAddressPostalCode = toAddress?.postalCode || parcelToPostcodeCustomer;
  const parcelTrackingUrl = customFields?.find(
    (item) => item.key === 'parcel_tracking_url',
  )?.value;

  // Replace placeholders with shipment data
  const formattedUrl = carrierUrlTracking
    .replace('{{Parcel.ID}}', parcelId ?? '')
    .replace('{{parcel_to_postcode_customer}}', toAddressPostalCode ?? '')
    .replace('{{parcel_alias_2}}', parcelAlias2 ?? '')
    .replace('{{parcel_tracking_url}}', parcelTrackingUrl ?? '')
    .replace(
      '{{LinkedParcel.ID}}',
      additionalData?.linkedShipmentTrackingNumber ?? '',
    );

  // Sanitize the final URL to prevent XSS attacks
  return sanitizeUrl(formattedUrl);
};

/**
 * Extracts linked shipment information from additional data
 * @param additionalData - Shipment additional data
 * @returns Linked shipment carrier details and validation flag
 */
export const extractLinkedShipmentInfo = (additionalData: any) => {
  const linkedShipmentCarrier =
    additionalData?.linkedShipmentCarrierOrg?.carrier;
  const linkedShipmentCarrierOrg = additionalData?.linkedShipmentCarrierOrg;
  const hasLinkedShipment = !!linkedShipmentCarrier;

  return { linkedShipmentCarrier, linkedShipmentCarrierOrg, hasLinkedShipment };
};

/**
 * Validates destination carrier and checks if latest event is from DC
 * @param destinationCarrier - Destination carrier from shipment
 * @param latestCarrier - Latest carrier from shipment events
 * @returns Destination carrier validation flags
 */
export const validateDestinationCarrier = (
  destinationCarrier: Carrier | undefined,
  latestCarrier: Carrier | undefined,
) => {
  const hasDestinationCarrier = !!destinationCarrier;
  const isLatestEventFromDestinationCarrier =
    latestCarrier?.id === destinationCarrier?.id;

  return { hasDestinationCarrier, isLatestEventFromDestinationCarrier };
};

/**
 * Determines if carrier switcher should be visible
 * @param hasLinkedShipment - Whether shipment has linked shipment
 * @param hasDestinationCarrier - Whether shipment has destination carrier
 * @param shouldShowDestinationCarrier - Flag to enable destination carrier display
 * @returns Boolean indicating switcher visibility
 */
export const computeSwitcherVisibility = (
  hasLinkedShipment: boolean,
  hasDestinationCarrier: boolean,
  shouldShowDestinationCarrier: boolean,
): boolean => {
  return (
    hasLinkedShipment || (hasDestinationCarrier && shouldShowDestinationCarrier)
  );
};
